import { CheckBadgeIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDebounce } from "use-debounce";

import { Input, Label, Tooltip } from "@fronterahealth/frontera-ui-components";

import { useUpdateLearnerFileEffectiveDateMutation } from "@api/graphql/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";

const DEBOUNCE_MS = 500;

interface EffectiveDateEditorProps {
  fileId: string;
  savedEffectiveDate: string;
  clinicianEditedAt: string | null;
  onSaved?: () => void | Promise<void>;
}

interface EffectiveDateFormValues {
  effectiveDate: string;
}

export const EffectiveDateEditor: React.FC<EffectiveDateEditorProps> = ({
  fileId,
  savedEffectiveDate,
  clinicianEditedAt,
  onSaved,
}) => {
  // `values` re-syncs the form when the server value changes (file navigation
  // or post-save refetch).
  const { register, watch, reset } = useForm<EffectiveDateFormValues>({
    values: { effectiveDate: savedEffectiveDate },
  });

  // eslint-disable-next-line react-hooks/incompatible-library -- RHF's watch() can't be memoized; compiler correctly skips this component
  const liveValue = watch("effectiveDate");
  const [debouncedValue] = useDebounce(liveValue, DEBOUNCE_MS);

  const { mutateAsync } = useUpdateLearnerFileEffectiveDateMutation();
  const queryClient = useQueryClient();

  // Fire a save whenever the debounced value diverges from what's persisted.
  useEffect(() => {
    if (debouncedValue === savedEffectiveDate) return;
    let cancelled = false;
    (async () => {
      try {
        await mutateAsync({ fileId, effectiveDate: debouncedValue || null });
        if (cancelled) return;
        await queryClient.invalidateQueries({ queryKey: ["GetLearnerFilesByLearner"] });
        await onSaved?.();
        reset({ effectiveDate: debouncedValue });
      } catch {
        if (!cancelled) notifyError("Failed to save effective date");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedValue, savedEffectiveDate, fileId, mutateAsync, onSaved, queryClient, reset]);

  return (
    <section className="flex flex-col">
      <div className="flex items-center justify-between">
        <Label displayType="loud" size="small">
          Effective Date
        </Label>
        {clinicianEditedAt && (
          <Tooltip text="Manually set by a clinician" showInfoIcon={false}>
            <CheckBadgeIcon className="h-4 w-4 text-icon-tertiary" aria-label="Manually set by a clinician" />
          </Tooltip>
        )}
      </div>
      <Input type="date" inputSize="full" label="" {...register("effectiveDate")} />
    </section>
  );
};
